import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { GraduationCap, ChevronDown, UserCheck, X } from "lucide-react";
import { API, useApp } from "../context";
import { useActiveLearner } from "../hooks/useActiveLearner";

// Persistent, site-wide "Signed in / Learning as" indicator. Only shown for
// parent-type accounts that have explorers. Makes it unmistakable which learner
// the learning tools (and arcade) are currently serving, and lets the parent
// switch or jump back to themselves from any page.
export default function LearnerHeaderChip() {
  const { user } = useApp();
  const { learner, setLearner, isSelf, learnerName } = useActiveLearner();
  const [explorers, setExplorers] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const role = (user?.role || "").toLowerCase();
  const isParentType = ["parent", "admin", "super_admin", "solo_explorer"].includes(role) || !role;

  useEffect(() => {
    if (!user?.id || !isParentType) return;
    axios.get(`${API}/family/me`).then(r => {
      setExplorers((r.data?.children || []).map(c => ({
        id: c.id, name: c.first_name || c.explorer_name || c.username || "Explorer",
      })));
    }).catch(() => {});
  }, [user?.id, isParentType]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // No explorers to switch into → nothing to disambiguate.
  if (!user || !isParentType || explorers.length === 0) return null;

  const selfName = user?.first_name || user?.nickname || user?.username || "Me";

  return (
    <div className="relative" ref={ref} data-testid="learner-header-chip">
      <button onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-bold transition-all ${
          isSelf ? "bg-white/[0.06] border-white/15 text-white/70 hover:text-white"
                 : "bg-amber-500/20 border-amber-400/40 text-amber-100"
        }`}
        data-testid="learner-chip-button" title="Choose who is learning">
        <GraduationCap size={13} />
        <span className="hidden sm:inline">{isSelf ? `Learning as ${selfName}` : `Learning as ${learnerName}`}</span>
        <span className="sm:hidden">{isSelf ? selfName : learnerName}</span>
        <ChevronDown size={12} />
      </button>

      {!isSelf && (
        <button onClick={() => setLearner(null)}
          className="ml-1 hidden md:inline-flex items-center gap-1 px-2 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-white/60 hover:text-white text-[11px] font-bold"
          data-testid="learner-back-to-me" title="Back to my own account">
          <X size={11} /> Back to me
        </button>
      )}

      {open && (
        <div className="fixed left-2 right-2 top-[64px] z-[70] max-h-[80vh] overflow-y-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-60 rounded-xl bg-[#141a2e] border border-white/10 shadow-2xl p-2" data-testid="learner-chip-menu">
          <div className="flex items-start justify-between gap-2">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest px-2 py-1">Signed in as {selfName}</p>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="learner-menu-close"
              className="shrink-0 w-6 h-6 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 flex items-center justify-center">
              <X size={14} />
            </button>
          </div>
          <p className="text-white/30 text-[10px] px-2 pb-1.5">Choose who to learn as. Parent Corner &amp; billing always stay yours.</p>
          <button onClick={() => { setLearner(null); setOpen(false); }}
            className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-bold ${isSelf ? "bg-emerald-500/20 text-white" : "text-white/70 hover:bg-white/5"}`}
            data-testid="learner-menu-self">
            <span>Me ({selfName})</span>{isSelf && <UserCheck size={14} className="text-emerald-400" />}
          </button>
          {explorers.map(ex => {
            const active = learner?.id === ex.id;
            return (
              <button key={ex.id} onClick={() => { setLearner({ id: ex.id, name: ex.name }); setOpen(false); }}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-bold ${active ? "bg-emerald-500/20 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid={`learner-menu-${ex.id}`}>
                <span>{ex.name}</span>{active && <UserCheck size={14} className="text-emerald-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
